import Grid2 from '@mui/material/Grid2';
import React, { useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import {
  EditProfileFormikValues,
  EditProfileFormikValuesKeys,
  EditProfileFormValueTypes,
} from 'layouts/EditProfile';
import { useFormikContext } from 'formik';
import { EditProfileProps } from 'layouts/EditProfile/EditProfile';
import { TextField, CreditType } from '@elevator/ui';
import SectionHeader from '../SectionHeader/SectionHeader';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  SelectChangeEvent,
  InputAdornment,
} from '@mui/material';
import { useQuery } from '@apollo/client/react';
import { CREDIT_TYPES } from 'lib/graphql/queries/creditTypes';
import { getDefaultSocialMediaPrefixes } from 'util/socials';
import {
  buildUrl,
  previewHrefForProfileField,
  type ProfileSocialField,
} from '@elevator/socialite';
import { SocialMediaType } from '@elevator/ui';
import Apple from '@mui/icons-material/Apple';
import Facebook from '@mui/icons-material/Facebook';
import Instagram from '@mui/icons-material/Instagram';
import YouTube from '@mui/icons-material/YouTube';
import XIcon from '@mui/icons-material/X';
import Soundcloud from 'mdi-material-ui/Soundcloud';
import useStyles from './styles';

const MAX_DESCRIPTION_CHAR_COUNT = 5000;

/**
 * ProfileForm Component
 *
 * Single form for editing profile: basic info (display name, role, bio) and
 * social media links. No accordions; sections use simple headers and dividers.
 * Requires Formik context from a parent (EditProfile layout).
 */
const ProfileForm = (props: ProfileFormProps) => {
  const { handleInputChange, handleTouchedFields } = props;
  const { classes } = useStyles();
  const { values, touched, errors, isSubmitting, setFieldValue, setFieldTouched } =
    useFormikContext<EditProfileFormikValues>();

  const {
    data,
    loading: loadingCreditTypes,
    error: creditTypesError,
  } = useQuery<any>(CREDIT_TYPES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const titleOptions = useMemo<Array<CreditType>>(() => {
    const creditTypes: CreditType[] = data?.creditTypes || [];
    const nullTitle: CreditType = {
      typeId: null,
      typeName: 'None',
    };
    return [nullTitle, ...creditTypes];
  }, [data?.creditTypes]);

  const onRoleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const selectedTypeName = event.target.value;
      const selectedCreditType = titleOptions.find(
        (option) => option.typeName === selectedTypeName,
      );
      if (selectedCreditType) {
        setFieldValue('title', selectedCreditType);
        setFieldTouched('title', true);
      }
    },
    [titleOptions, setFieldValue, setFieldTouched],
  );

  const getSocialLinkPreview = useCallback((type: SocialMediaType, value: string) => {
    const emptyValue = value === '';
    const profileField = (type === 'x' ? 'twitter' : type) as ProfileSocialField;
    const prefix = getDefaultSocialMediaPrefixes(type);

    let displayText: string;
    let href: string | undefined;

    if (emptyValue) {
      displayText = type === 'youtube' ? 'Paste YouTube URL' : prefix;
      href = undefined;
    } else if (type === 'youtube') {
      displayText = previewHrefForProfileField('youtube', value) || value;
      href =
        displayText.startsWith('http') || displayText.startsWith('//')
          ? displayText.startsWith('//')
            ? `https:${displayText}`
            : displayText
          : undefined;
    } else if (type === 'spotify') {
      displayText =
        previewHrefForProfileField('spotify', value) ||
        buildUrl(value, 'spotify', 'artist');
      href = displayText.startsWith('http') ? displayText : undefined;
    } else if (type === 'appleMusic') {
      displayText =
        previewHrefForProfileField('appleMusic', value) || `${prefix}${value}`;
      href = displayText.startsWith('http') ? displayText : `https://${displayText}`;
    } else {
      displayText =
        previewHrefForProfileField(profileField, value) ||
        buildUrl(value, type === 'x' ? 'x' : type);
      href = displayText.startsWith('http') ? displayText : undefined;
    }

    return (
      <Typography
        component={emptyValue ? 'span' : 'a'}
        href={href}
        target={emptyValue ? undefined : '_blank'}
        rel={emptyValue ? undefined : 'noopener noreferrer'}
        variant="caption"
        color={emptyValue ? 'textSecondary' : 'primary'}
      >
        {displayText.replace(/^(https?:\/\/)?(www\.)?/i, '').replace(/^\/\//, '')}
      </Typography>
    );
  }, []);

  const handleSocialInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const fieldName = event.target.id as EditProfileFormikValuesKeys;
      const { value } = event.target;
      setFieldValue(fieldName, value, true);
      handleTouchedFields(fieldName, event.target.value);
      setFieldTouched(fieldName, true, false);
    },
    [setFieldValue, handleTouchedFields, setFieldTouched],
  );

  return (
    <Box data-component="ProfileForm" className={classes.root}>
      {/* Basic Info */}
      <section className={classes.section}>
        <SectionHeader title="Basic Info" titleVariant="h6" divider={false} />
        <Grid2 container spacing={1} className={classes.gridContainer}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              id="displayName"
              label="Display Name"
              aria-label="Display name for your profile"
              fullWidth
              required
              value={values.displayName}
              onChange={(event) => handleInputChange('displayName', event)}
              onBlur={(event) => handleTouchedFields('displayName', event.target.value)}
              error={touched.displayName && Boolean(errors.displayName)}
              helperText={touched.displayName ? errors.displayName : null}
              disabled={isSubmitting}
              disableUnderline
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth variant="filled">
              <InputLabel id="typeLabel" shrink>
                Role
              </InputLabel>
              <Select
                id="title"
                labelId="typeLabel"
                label="Role"
                variant="filled"
                fullWidth
                disableUnderline
                value={
                  loadingCreditTypes ||
                  !titleOptions.some((opt) => opt.typeName === values.title?.typeName)
                    ? ''
                    : values.title?.typeName || ''
                }
                onChange={onRoleChange}
                disabled={isSubmitting || loadingCreditTypes}
                displayEmpty
                aria-label="Select your role or credit type"
              >
                {loadingCreditTypes ? (
                  <MenuItem disabled>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} />
                      <ListItemText primary="Loading roles..." />
                    </Box>
                  </MenuItem>
                ) : creditTypesError ? (
                  <MenuItem disabled>
                    <ListItemText
                      primary="Error loading roles"
                      secondary="Please refresh the page"
                    />
                  </MenuItem>
                ) : (
                  titleOptions.map((title) => (
                    <MenuItem key={title.typeName} value={title.typeName}>
                      <ListItemText primary={title.typeName} />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid2>
          <Grid2 size={12} className={classes.textFieldContainer}>
            <TextField
              id="bio"
              label="Bio"
              placeholder="Tell us about yourself"
              aria-label="Bio or description about yourself"
              multiline
              minRows={10}
              fullWidth
              required
              value={values.bio}
              onChange={(event) => handleInputChange('bio', event)}
              onBlur={(event) => handleTouchedFields('bio', event.target.value)}
              slotProps={{
                input: { inputProps: { maxLength: MAX_DESCRIPTION_CHAR_COUNT } },
              }}
              error={touched.bio && Boolean(errors.bio)}
              helperText={
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  component="span"
                >
                  <Typography
                    variant="caption"
                    color={errors.bio ? 'error' : 'text.secondary'}
                  >
                    {touched.bio ? errors.bio : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {values.bio ? values.bio.length : 0} / {MAX_DESCRIPTION_CHAR_COUNT}
                  </Typography>
                </Box>
              }
              disabled={isSubmitting}
              disableUnderline
            />
          </Grid2>
        </Grid2>
      </section>

      {/* Social Media */}
      <section className={classes.section}>
        <SectionHeader title="Social Media" titleVariant="h6" divider={false} />
        <Box className={classes.socialsContainer}>
          <TextField
            id="facebook"
            fullWidth
            placeholder="Username or Profile URL"
            startAdornment={
              <InputAdornment position="start">
                <Facebook color="disabled" fontSize="small" />
              </InputAdornment>
            }
            label="Facebook"
            aria-label="Facebook username or profile URL"
            value={values.facebook}
            onChange={handleSocialInputChange}
            onBlur={(event) => handleTouchedFields('facebook', event.target.value)}
            error={touched.facebook && Boolean(errors.facebook)}
            helperText={
              errors.facebook || getSocialLinkPreview('facebook', values.facebook)
            }
            disabled={isSubmitting}
            disableUnderline
          />
          <TextField
            id="instagram"
            fullWidth
            placeholder="Username or Profile URL"
            startAdornment={
              <InputAdornment position="start">
                <Instagram color="disabled" fontSize="small" />
              </InputAdornment>
            }
            label="Instagram"
            aria-label="Instagram username or profile URL"
            value={values.instagram}
            onChange={handleSocialInputChange}
            onBlur={(event) => handleTouchedFields('instagram', event.target.value)}
            error={touched.instagram && Boolean(errors.instagram)}
            helperText={
              errors.instagram || getSocialLinkPreview('instagram', values.instagram)
            }
            disabled={isSubmitting}
            disableUnderline
          />
          <TextField
            id="twitter"
            label="X"
            aria-label="X handle or profile URL"
            value={values.twitter}
            fullWidth
            placeholder="Username or Profile URL"
            startAdornment={
              <InputAdornment position="start">
                <XIcon sx={{ color: 'action.disabled' }} fontSize="small" />
              </InputAdornment>
            }
            onChange={handleSocialInputChange}
            onBlur={(event) => handleTouchedFields('twitter', event.target.value)}
            error={touched.twitter && Boolean(errors.twitter)}
            helperText={errors.twitter || getSocialLinkPreview('x', values.twitter)}
            disabled={isSubmitting}
            disableUnderline
          />
          <TextField
            id="soundcloud"
            label="SoundCloud"
            aria-label="SoundCloud username"
            value={values.soundcloud}
            placeholder="Username or Profile URL"
            startAdornment={
              <InputAdornment position="start">
                <Soundcloud sx={{ color: 'action.disabled' }} fontSize="small" />
              </InputAdornment>
            }
            fullWidth
            onChange={handleSocialInputChange}
            onBlur={(event) => handleTouchedFields('soundcloud', event.target.value)}
            error={touched.soundcloud && Boolean(errors.soundcloud)}
            helperText={
              errors.soundcloud || getSocialLinkPreview('soundcloud', values.soundcloud)
            }
            disabled={isSubmitting}
            disableUnderline
          />
          <TextField
            id="youtube"
            label="YouTube"
            aria-label="YouTube handle or profile URL"
            value={values.youtube}
            placeholder="Handle or Profile URL"
            startAdornment={
              <InputAdornment position="start">
                <YouTube color="disabled" fontSize="small" />
              </InputAdornment>
            }
            fullWidth
            onChange={handleSocialInputChange}
            onBlur={(event) => handleTouchedFields('youtube', event.target.value)}
            error={touched.youtube && Boolean(errors.youtube)}
            helperText={errors.youtube || getSocialLinkPreview('youtube', values.youtube)}
            disabled={isSubmitting}
            disableUnderline
          />
          <TextField
            id="appleMusic"
            label="Apple Music"
            aria-label="Apple Music profile URL"
            value={values.appleMusic}
            placeholder="Profile URL"
            startAdornment={
              <InputAdornment position="start">
                <Apple color="disabled" fontSize="small" />
              </InputAdornment>
            }
            fullWidth
            onChange={handleSocialInputChange}
            onBlur={(event) => handleTouchedFields('appleMusic', event.target.value)}
            error={touched.appleMusic && Boolean(errors.appleMusic)}
            helperText={
              errors.appleMusic || getSocialLinkPreview('appleMusic', values.appleMusic)
            }
            disabled={isSubmitting}
            disableUnderline
          />
        </Box>
      </section>
    </Box>
  );
};

export type ProfileFormProps = {
  handleInputChange: (
    fieldName: EditProfileFormikValuesKeys,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | null,
    imperativeValue?: EditProfileFormValueTypes,
  ) => void;
} & Pick<EditProfileProps, 'handleTouchedFields'>;

export default ProfileForm;
